import { Injectable, NgZone } from '@angular/core';
import { auth } from 'firebase/app';
import { AngularFireAuth } from "@angular/fire/auth";
import { Router } from "@angular/router";
import { UsuarioService } from './usuario.service';
import { Usuario } from 'src/app/models/entities/usuario';
import { first, take } from 'rxjs/operators';
import { UsuarioEnSesionService } from './usuario-en-sesion.service';
import { LoaderService } from './loader.service';
import { AlertasService } from './alertas.service';
import { TransaccionEnum } from 'src/app/shared/enums/transaccion.enum';
import { FacadeSesionService } from './facade-sesion.service';
import { UsuarioRolService } from './usuario-rol.service';
import { UsuarioRol } from 'src/app/models/entities/usuario-rol';
import { RolService } from './rol.service';
import { ElementoCatalogo } from 'src/app/models/entities/elemento-catalogo';
import { FacadeUsuarioService } from './facade-usuario.service';
import { UsuarioSesionDTO } from 'src/app/models/dtos/usuario-sesion-dto';
import { RegistroUsuarioDTO } from 'src/app/models/dtos/registro-usuario-dto';

@Injectable({
  providedIn: 'root'
})
export class AutenticacionService {

  constructor(
    private afAuth: AngularFireAuth, 
    private router: Router,
    private usuarioService: UsuarioService,
    private rolService: RolService,
    private usuarioRolService: UsuarioRolService,
    private alertasService: AlertasService,
    private loaderService: LoaderService,
    private facadeSesionService: FacadeSesionService,
    private facadeUsuarioService: FacadeUsuarioService
  ) {
    this.validarUsuarioConSesionAbierta();
  }

  public async validarUsuarioConSesionAbierta() {
    try {
      this.loaderService.activarLoaderScreen();
      let firebaseUser: firebase.User = await this.afAuth.authState.pipe(first()).toPromise();
      if (firebaseUser && firebaseUser.emailVerified) {
        let usuarioConRol: UsuarioSesionDTO = await this.facadeUsuarioService.obtenerDatosUsuario(firebaseUser.email).pipe(first()).toPromise();
        if (usuarioConRol) {
          this.facadeSesionService.usuarioEnSesionService.actualizarUsuario(usuarioConRol);
          this.router.navigate(["/seleccion-modulos/lista-modulos-principales"], { skipLocationChange: true });
        } else {
          this.alertasService.mostrarAlerta('error', 'El correo no existe', null);
          this.router.navigate(["/inicio-sesion"], { skipLocationChange: true });
        }
      } else {
        this.router.navigate(["/inicio-sesion"], { skipLocationChange: true });
      }
    } catch (error) {
      this.alertasService.mostrarAlerta('error', null, error);
      this.router.navigate(["/inicio-sesion"], { skipLocationChange: true });
    } finally {
      this.loaderService.desactivarLoaderScreen();
    }

  }


  public async iniciarSesion(email: string, password: string) {
    try {
      this.loaderService.activarLoaderScreen();
      let result: auth.UserCredential = await this.afAuth.auth.signInWithEmailAndPassword(email, password);
      if (result.user.emailVerified) {
        let usuarioConRol: UsuarioSesionDTO = await this.facadeUsuarioService.obtenerDatosUsuario(result.user.email).pipe(first()).toPromise();
        if (usuarioConRol) {
          this.facadeSesionService.usuarioEnSesionService.actualizarUsuario(usuarioConRol);
          this.router.navigate(['/seleccion-modulos/lista-modulos-principales'], { skipLocationChange: true });
        } else {
          this.alertasService.mostrarAlerta('error', 'El correo no existe.', null);
          this.router.navigate(["/inicio-sesion"], { skipLocationChange: true });
        }
      } else {
        this.alertasService.mostrarAlerta('error', null, "El correo aún no se ha verificado. ");
        this.router.navigate(["/inicio-sesion"], { skipLocationChange: true });
      }
    } catch (error) {
      this.alertasService.mostrarAlerta('error', null, error);
      this.router.navigate(["/inicio-sesion"], { skipLocationChange: true });
    } finally {
      this.loaderService.desactivarLoaderScreen();
    }
  }

  public async registrarUsuario(usuarioRegistro: RegistroUsuarioDTO): Promise<TransaccionEnum> {
    try {
      this.loaderService.activarLoaderScreen();
      await this.afAuth.auth.createUserWithEmailAndPassword(usuarioRegistro.correo, usuarioRegistro.contrasena);
      delete usuarioRegistro.contrasena;
      delete usuarioRegistro.confirmarContrasena;
      let usuario: Usuario = {
        ...usuarioRegistro,
        nombre: usuarioRegistro.nombre.toUpperCase(),
        primerApellido: usuarioRegistro.primerApellido.toUpperCase(),
        segundoApellido: usuarioRegistro.segundoApellido.toUpperCase(),
        estatus: true
      };
      let usuarioCreado = await this.usuarioService.save(usuario);
      // Hasta aqui todo va bien!!----
      let rolUsuarioGeneral: ElementoCatalogo = await this.rolService.obtenerRolUsuarioGeneral().pipe(take(1)).toPromise();
      let usuarioRol: UsuarioRol = {
        keyUsuario: usuarioCreado.key,
        keyRol: rolUsuarioGeneral.key,
        fechaActualizacion: new Date().getTime(),
        fechaCreacion: new Date().getTime()
      };
      
      await this.usuarioRolService.save(usuarioRol);
      this.alertasService.mostrarAlerta('success', "Usuario creado con éxito", "Se ha enviado un correo de verificación para activar su cuenta.");
      await this.afAuth.auth.currentUser.sendEmailVerification();
      return TransaccionEnum.EXITOSA;
    } catch (error) {
      this.alertasService.mostrarAlerta('error', null, error);
      return TransaccionEnum.FALLIDA;
    } finally {
      this.loaderService.desactivarLoaderScreen();
    }
  }

  public async enviarCorreoReinicioPassword(passwordResetEmail: string) {
    try {
      await this.afAuth.auth.sendPasswordResetEmail(passwordResetEmail);
      this.alertasService.mostrarAlerta('info', null, 'Un correo electrónico de restablecimiento de contraseña se ha enviado, revise su bandeja de entrada.');
    } catch (error) {
      this.alertasService.mostrarAlerta('error', null, error);
    }
  }


  public async cerrarSesion() {
    try {
      await this.afAuth.auth.signOut();
      this.facadeSesionService.inicializar();
      this.router.navigate(['/inicio-sesion'], { skipLocationChange: true });
    } catch (error) {
      this.alertasService.mostrarAlerta('error', null, error);
    }
  }

}
